import axios from 'axios';
import { useAuthStore } from '../stores/authStore';

export const api = axios.create({
  baseURL: '/api',
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

const LOGIN_ROUTE = '/login';

// A 401 on these endpoints is expected rather than a dead session: a wrong password on
// the login form, or a logout for a session that is already gone. Redirecting on them
// would fight the login page.
const AUTH_ENDPOINTS = ['/auth/login', '/auth/logout'];

/** Extracts a human-readable (Russian) message from a rejected request. */
export function getApiErrorMessage(error: unknown, fallback = 'Ошибка запроса'): string {
  if (axios.isAxiosError<{ error?: string }>(error)) {
    return error.response?.data?.error || error.message || fallback;
  }
  if (error instanceof Error) return error.message;
  return fallback;
}

/** True when the request failed with 404 — including ownership guards. */
export function isNotFoundError(error: unknown): boolean {
  return axios.isAxiosError(error) && error.response?.status === 404;
}

api.interceptors.response.use(
  (response) => response,
  (error: unknown) => {
    if (axios.isAxiosError(error)) {
      const status = error.response?.status;
      const url = error.config?.url ?? '';

      if (status === 401 && !AUTH_ENDPOINTS.some((endpoint) => url.includes(endpoint))) {
        // The session died (expired cookie, server restart). Drop the cached user so
        // guards don't keep rendering a stale session, then send the browser to the
        // login route — ProtectedRoute cannot recover from a session that dies while
        // the app is already mounted.
        useAuthStore.getState().logout();
        // Guard against a redirect loop: on the login page there is nothing to do.
        if (window.location.pathname !== LOGIN_ROUTE) {
          window.location.replace(LOGIN_ROUTE);
        }
      }
    }

    return Promise.reject(error);
  },
);